/* eslint @typescript-eslint/no-explicit-any: 0 */
import {useState} from 'react';
import {Box, Grid, Button, makeStyles, Modal, CircularProgress, TextField, MenuItem} from '@material-ui/core';
import {PersonOutlineOutlined} from '@material-ui/icons';
import {z} from 'zod';
import {Controller, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {PolicyRequirementStatus, PolicyRequirementType, Requirement} from '../../APIs/CustomerPortalAPI';
import {PolicyRequirementTypeOptions} from './SelectOptions';
import AllStatus from '../Underwritings/AllStatus';
import {useDocusignEnvelopeForRequirementMutation} from './hooks/useDocusignEnvelopeForRequirementMutation';

interface PolicyDocumentFormProps {
    index: number;
    value: Requirement;
}

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const useStyles = makeStyles((theme) => ({
    paper: {
        position: 'absolute',
        width: 400,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(2, 4, 3),
        borderRadius: 5,
    },
}));

type DocusignModalProps = {
    open: boolean;
    onConfirmSaveAndContinue: () => Promise<void>;
    onClose: () => void;
};

function DocusignModal({onConfirmSaveAndContinue, onClose, open}: DocusignModalProps) {
    const classes = useStyles();
    const [modalStyle] = useState(getModalStyle);
    const [loading, setLoading] = useState(false);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <div style={modalStyle} className={classes.paper}>
                <h4 id="modal-modal-title">Save + Launch Docusign</h4>
                <p id="modal-modal-description" className="modal-text">
                    Let&apos;s continue adding this document in Docusign. This action will save all changes made to this
                    page.
                </p>
                <br />
                <Grid container spacing={1} direction="row" alignItems="center">
                    <Grid item xs={6}>
                        <Button
                            id="save_and_continue_button"
                            className="modal-button"
                            style={{backgroundColor: '#3B58B8', color: 'white'}}
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                                setLoading(true);
                                try {
                                    await onConfirmSaveAndContinue();
                                } finally {
                                    setLoading(false);
                                }
                            }}
                        >
                            {!loading ? (
                                <>Save and Continue</>
                            ) : (
                                <CircularProgress size={20} style={{color: 'white'}} />
                            )}
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            id="cancel_button"
                            className="modal-button"
                            variant="outlined"
                            color="primary"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    </Grid>
                </Grid>
            </div>
        </Modal>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PolicyRequirementSchema = z.object({
    type: z.nativeEnum(PolicyRequirementType).transform(String),
    status: z.nativeEnum(PolicyRequirementStatus).transform(String),
    internalNote: z.string().optional(),
});

type PolicyRequirementFormValues = z.infer<typeof PolicyRequirementSchema>;

function PolicyDocumentForm({index, value}: PolicyDocumentFormProps) {
    const [openConfirmation, setOpenConfirmation] = useState(false);

    const {register, handleSubmit, control, formState, reset} = useForm<PolicyRequirementFormValues>({
        resolver: zodResolver(PolicyRequirementSchema),
        defaultValues: {
            ...value,
            internalNote: value?.internalNote ?? '',
        },
    });

    const {mutateAsync: mutateDocusignEnvelopeForRequirement, isLoading} = useDocusignEnvelopeForRequirementMutation();

    const onOpenConfirmation = () => {
        setOpenConfirmation(true);
    };

    const onCloseConfirmation = () => {
        setOpenConfirmation(false);
    };

    const onSaveRequirement = handleSubmit(async (data) => {
        const {redirectUrl} = await mutateDocusignEnvelopeForRequirement({
            ...value,
            ...data,
            dsReturnUrl: window.location.href,
        });

        reset();

        window.location.href = redirectUrl;
    });

    return (
        <>
            <Grid container spacing={1} direction="row" alignItems="center">
                <Grid item>
                    <PersonOutlineOutlined fontSize="small" />
                </Grid>
                <Grid item>
                    <h4>Policy Signing Requirement</h4>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={8} direction="row" alignItems="center">
                        <Grid item xs={3}>
                            <Controller
                                name="type"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        id={`requirement_type_${index}`}
                                        label="Requirement"
                                        {...field}
                                        placeholder="--"
                                        variant="standard"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        error={!!formState.errors.type}
                                        helperText={formState.errors.type?.message}
                                        select
                                        fullWidth
                                    >
                                        {PolicyRequirementTypeOptions.map((option: any) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                name="status"
                                control={control}
                                render={({field}) => (
                                    <TextField
                                        id={`status_${index}`}
                                        label="Status"
                                        {...field}
                                        placeholder="--"
                                        variant="standard"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        error={!!formState.errors.status}
                                        helperText={formState.errors.status?.message}
                                        select
                                        fullWidth
                                    >
                                        {AllStatus.map((option) => (
                                            <MenuItem key={option.name} value={option.name}>
                                                {option.description}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={5}>
                            <TextField
                                id={`notes_${index}`}
                                label="Notes"
                                {...register('internalNote')}
                                placeholder="--"
                                multiline
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                error={!!formState.errors.internalNote}
                                helperText={formState.errors.internalNote?.message}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Box>
                <Grid container spacing={3} xs={12}>
                    <Grid item xs={12}>
                        <Grid container justify="flex-end">
                            <Grid item>
                                {value.dataJson && value.dataJson?.envelopeId ? (
                                    <Button
                                        id={`doc_button_${index}`}
                                        variant="outlined"
                                        color="primary"
                                        onClick={onSaveRequirement}
                                    >
                                        {!isLoading ? <>Save Requirement</> : <CircularProgress size={30} />}
                                    </Button>
                                ) : (
                                    <Button
                                        id={`doc_button_${index}`}
                                        variant="outlined"
                                        color="primary"
                                        onClick={onOpenConfirmation}
                                    >
                                        Save + Launch Docusign
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <DocusignModal
                    open={openConfirmation}
                    onConfirmSaveAndContinue={onSaveRequirement}
                    onClose={onCloseConfirmation}
                />
            </Box>
        </>
    );
}

export default PolicyDocumentForm;
